<template>
    
  <div class="dashboard">
    <!-- Header -->
    <header class="header">
      <div class="container">
        <div class="header-content">
          <h1>Hello Evano 👋</h1>
          <div class="search-box">
            <i class="bi bi-search"></i>
            <input type="text" placeholder="Search" v-model="searchQuery">
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="container main-content">
      <!-- Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-icon icon-green">
              <i class="bi bi-people"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">Total Customers</div>
              <div class="stat-value">5,423</div>
              <div class="stat-change change-up">
                <i class="bi bi-arrow-up"></i> 16% this month
              </div>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-icon icon-green">
              <i class="bi bi-person-check"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">Members</div>
              <div class="stat-value">1,893</div>
              <div class="stat-change change-down">
                <i class="bi bi-arrow-down"></i> 1% this month
              </div>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-content">
            <div class="stat-icon icon-purple">
              <i class="bi bi-display"></i>
            </div>
            <div class="stat-info">
              <div class="stat-label">Active Now</div>
              <div class="stat-value">189</div>
              <div class="avatar-group">
                <div class="avatar" v-for="(avatar, index) in avatars" :key="index">
                  {{ avatar }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Customers Table -->
      <div class="customers-section">
        <div class="section-header">
          <div>
            <h2 class="section-title">All Customers</h2>
            <a href="#" class="active-members">Active Members</a>
          </div>
        </div>

        <div class="search-sort-bar">
          <div class="search-box">
            <i class="bi bi-search"></i>
            <input type="text" placeholder="Search" v-model="tableSearch">
          </div>
          <div class="sort-wrapper">
            <label>Start by:</label>
            <select class="sort-select" v-model="sortBy">
              <option>Newest</option>
              <option>Oldest</option>
              <option>Name</option>
            </select>
          </div>
        </div>

        <div class="table-wrapper">
          <table class="customers-table">
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>Company</th>
                <th>Phone Number</th>
                <th>Email</th>
                <th>Country</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="customer in paginatedCustomers" :key="customer.email">
                <td><strong>{{ customer.name }}</strong></td>
                <td>{{ customer.company }}</td>
                <td>{{ customer.phone }}</td>
                <td>{{ customer.email }}</td>
                <td>{{ customer.country }}</td>
                <td>
                  <span :class="['status-badge', customer.status === 'Active' ? 'status-active' : 'status-inactive']">
                    {{ customer.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination">
          <div class="pagination-info">
            Showing data 1 to 8 of 256K entries
          </div>
          <div class="pagination-controls">
            <button class="page-btn" @click="prevPage" :disabled="currentPage === 1">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button 
              v-for="page in visiblePages" 
              :key="page"
              :class="['page-btn', { active: currentPage === page }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
            <button class="page-btn" v-if="totalPages > 5">...</button>
            <button class="page-btn" @click="currentPage = 40">40</button>
            <button class="page-btn" @click="nextPage" :disabled="currentPage === totalPages">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomerDashboard',
  data() {
    return {
      searchQuery: '',
      tableSearch: '',
      sortBy: 'Newest',
      currentPage: 1,
      itemsPerPage: 8,
      avatars: ['👤', '👨', '👩', '🧑'],
      customers: [
        { name: 'Jane Cooper', company: 'Microsoft', phone: '(225) 555-0118', email: 'jane@microsoft.com', country: 'United States', status: 'Active' },
        { name: 'Floyd Miles', company: 'Yahoo', phone: '(205) 555-0100', email: 'floyd@yahoo.com', country: 'Kiribati', status: 'Inactive' },
        { name: 'Ronald Richards', company: 'Adobe', phone: '(302) 555-0107', email: 'ronald@adobe.com', country: 'Israel', status: 'Inactive' },
        { name: 'Marvin McKinney', company: 'Tesla', phone: '(252) 555-0126', email: 'marvin@tesla.com', country: 'Iran', status: 'Active' },
        { name: 'Jerome Bell', company: 'Google', phone: '(629) 555-0129', email: 'jerome@google.com', country: 'Reunion', status: 'Active' },
        { name: 'Kathryn Murphy', company: 'Microsoft', phone: '(406) 555-0120', email: 'kathryn@microsoft.com', country: 'Curaçao', status: 'Active' },
        { name: 'Jacob Jones', company: 'Yahoo', phone: '(208) 555-0112', email: 'jacob@yahoo.com', country: 'Brazil', status: 'Active' },
        { name: 'Kristin Watson', company: 'Facebook', phone: '(704) 555-0127', email: 'kristin@facebook.com', country: 'Åland Islands', status: 'Inactive' }
      ]
    }
  },
  computed: {
    filteredCustomers() {
      let filtered = this.customers;
      
      if (this.tableSearch) {
        const search = this.tableSearch.toLowerCase();
        filtered = filtered.filter(c => 
          c.name.toLowerCase().includes(search) ||
          c.company.toLowerCase().includes(search) ||
          c.email.toLowerCase().includes(search) ||
          c.country.toLowerCase().includes(search)
        );
      }
      
      return filtered;
    },
    paginatedCustomers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCustomers.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredCustomers.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      for (let i = 1; i <= Math.min(maxVisible, this.totalPages); i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    }
  }
}
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background-color: #f5f6fa;
  
}

.header {
  background: white;
  padding: 20px 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.search-box {
  position: relative;
  width: 300px;
}

.search-box input {
  width: 100%;
  padding: 10px 10px 10px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: #f9fafb;
  outline: none;
}

.search-box input:focus {
  border-color: #6366f1;
}

.search-box i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.main-content {
  padding-top: 40px;
  padding-bottom: 40px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 40px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.icon-green {
  background: #d4f4dd;
  color: #00ac4f;
}

.icon-purple {
  background: #e5d9f2;
  color: #8b5cf6;
}

.stat-info {
  flex: 1;
}

.stat-label {
  color: #9ca3af;
  font-size: 14px;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 8px 0;
}

.stat-change {
  font-size: 13px;
  font-weight: 500;
}

.change-up {
  color: #00ac4f;
}

.change-down {
  color: #fa5a7d;
}

.avatar-group {
  display: flex;
  margin-top: 8px;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid white;
  margin-left: -8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.avatar:first-child {
  margin-left: 0;
}

.customers-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.active-members {
  color: #16c098;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
}

.search-sort-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sort-wrapper label {
  color: #9ca3af;
  font-size: 14px;
  margin: 0;
}

.sort-select {
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  background: white;
  cursor: pointer;
  outline: none;
}

.sort-select:focus {
  border-color: #6366f1;
}

.table-wrapper {
  overflow-x: auto;
}

.customers-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.customers-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
}

.customers-table tbody td {
  padding: 16px;
  font-size: 14px;
  color: #1a1a1a;
  border-bottom: 1px solid #f3f4f6;
}

.customers-table tbody tr:hover {
  background: #f9fafb;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  display: inline-block;
  min-width: 70px;
  text-align: center;
}

.status-active {
  background: #d4f4dd;
  color: #00ac4f;
  border: 1px solid #00ac4f;
}

.status-inactive {
  background: #ffe2e5;
  color: #fa5a7d;
  border: 1px solid #fa5a7d;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.pagination-info {
  color: #9ca3af;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.page-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: #6b7280;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #6366f1;
  color: #6366f1;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.active {
  background: #6366f1;
  color: white;
  border-color: #6366f1;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .search-sort-bar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .search-box {
    width: 100%;
  }
  
  .table-wrapper {
    overflow-x: scroll;
  }
}
</style>

<style scoped>
/* Global styles - ใส่ใน main.css หรือ App.vue */
@import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css');

* {
  box-sizing: border-box;
}

/* body {
  margin: 0;
  padding: 0;
} */
</style>